/**
 * GraphQL queries and hooks — Task pillar (issue #1680, epic #1674).
 *
 * Reads for the generic, cross-pillar `Task` primitive (issue #1678's
 * `task`/`tasks` queries). Writes are REST-only — see `lib/apiClient/task.ts`.
 */

import { gql } from '@apollo/client';
import { useQuery, useApolloClient, type QueryResult } from '@apollo/client/react';
import { useCallback, useEffect, useState } from 'react';
import type { Task, TaskConnection, TaskFilter } from '../types/task';

const TASK_FIELDS = gql`
  fragment TaskFields on Task {
    entityId
    entityType
    taskType
    name
    description
    subjectEntityType
    subjectEntityId
    assigneeType
    assigneeId
    audienceType
    audienceIds
    audienceCascadeEntityType
    audienceCascadeEntityId
    ownerType
    ownerId
    status
    cadence
    period
    isScheduled
    isDefaultFromPrior
    dueDate
    overdue
    output
    resultEntityType
    resultEntityId
    responseCount
    completionPercentage
    approvedBy
    approvedAt
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
  }
`;

export const GET_TASK = gql`
  ${TASK_FIELDS}
  query GetTask($id: ID!) {
    task(id: $id) {
      ...TaskFields
    }
  }
`;

export const LIST_TASKS = gql`
  ${TASK_FIELDS}
  query ListTasks($filter: TaskFilter, $limit: Int) {
    tasks(filter: $filter, limit: $limit) {
      edges {
        node {
          ...TaskFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const MY_OPEN_TASKS = gql`
  ${TASK_FIELDS}
  query MyOpenTasks($userId: ID!, $teamIds: [ID!]!, $limit: Int) {
    myOpenTasks(userId: $userId, teamIds: $teamIds, limit: $limit) {
      edges {
        node {
          ...TaskFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useTask(id: string): QueryResult<{ task: Task | null }> {
  return useQuery(GET_TASK, {
    variables: { id },
    skip: !id,
  });
}

export function useTasks(
  filter?: TaskFilter,
  limit: number = 50,
  skip: boolean = false
): QueryResult<{ tasks: TaskConnection }> {
  return useQuery(LIST_TASKS, {
    variables: { filter, limit },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip,
  });
}

export interface MyOpenTasksResult {
  tasks: Task[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Every `Open` or `InProgress` Task assigned to the current user directly
 * or to any team they belong to — the "My Tasks" badge/widget's data
 * source. Both statuses are included because the detail page's
 * `isRespondable` (`routes/tasks/$taskId.tsx`) treats `InProgress` as still
 * respondable; excluding it here would silently drop Tasks the assignee can
 * still act on from the badge count and list.
 *
 * Resolved via the dedicated `myOpenTasks` query (issue #1774), which does
 * the per-assignee fan-out server-side in one Lambda invocation. This hook
 * previously issued one `tasks` query per (assignee, status) pair itself —
 * `(1 + teamIds.length) * 2` parallel network requests per mount, fired
 * independently from both the always-mounted `TaskBadge` and this page —
 * which was throttling the graphql Lambda under concurrent dev traffic.
 * Does not surface multi-respondent (`audienceType`) Tasks targeting the
 * user's team via `TARGETS` edges — no audience-scoped query exists today;
 * see the Task list page's own note on this gap.
 */
export function useMyOpenTasks(
  userId: string | undefined,
  teamIds: readonly string[]
): MyOpenTasksResult {
  const client = useApolloClient();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const teamsKey = teamIds.join(',');

  const load = useCallback(() => {
    if (!userId) {
      setTasks([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    client
      .query<{ myOpenTasks: TaskConnection }>({
        query: MY_OPEN_TASKS,
        variables: { userId, teamIds, limit: 100 },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        setTasks(result.data?.myOpenTasks.edges.map((edge) => edge.node) ?? []);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error('Failed to load My Tasks'));
        setLoading(false);
      });
    // teamIds is captured by content via teamsKey, not by reference (this
    // file is a plain .ts module, outside the react-hooks lint scope, which
    // only covers frontend/**/*.tsx per eslint.config.js).
  }, [client, userId, teamsKey]);

  useEffect(() => {
    load();
  }, [load]);

  return { tasks, loading, error, refetch: load };
}
